import {useCallback, useEffect, useState} from 'react';

export interface SavedSearch {
  id: string;
  name: string;
  query: string;
}

const getStorageKey = (uid: string) => `todo_saved_searches_${uid}`;

export default function useSavedSearches(uid: string | undefined) {
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);

  useEffect(() => {
    if (!uid) {
      setSavedSearches([]);
      return;
    }

    try {
      const stored = localStorage.getItem(getStorageKey(uid));
      if (stored) {
        const parsed = JSON.parse(stored) as SavedSearch[];
        setSavedSearches(parsed);
      }
    } catch (error) {
      console.error('Error loading saved searches:', error);
    }
  }, [uid]);

  const saveSearch = useCallback(
    (name: string, query: string) => {
      if (!uid) return;

      const newSearch: SavedSearch = {
        id: crypto.randomUUID(),
        name,
        query,
      };

      const updated = [...savedSearches, newSearch];
      setSavedSearches(updated);

      try {
        localStorage.setItem(getStorageKey(uid), JSON.stringify(updated));
      } catch (error) {
        console.error('Error saving search:', error);
      }
    },
    [uid, savedSearches],
  );

  const deleteSearch = useCallback(
    (id: string) => {
      if (!uid) return;

      const updated = savedSearches.filter((search) => search.id !== id);
      setSavedSearches(updated);

      try {
        localStorage.setItem(getStorageKey(uid), JSON.stringify(updated));
      } catch (error) {
        console.error('Error deleting search:', error);
      }
    },
    [uid, savedSearches],
  );

  const loadSearch = useCallback(
    (id: string): string | null => {
      const search = savedSearches.find((s) => s.id === id);
      return search?.query ?? null;
    },
    [savedSearches],
  );

  return {
    savedSearches,
    saveSearch,
    deleteSearch,
    loadSearch,
  };
}
